// blocks/src/dashboard/project_data/projects_data_store.ts

import { AccountStore } from 'base/account_store';
import { AnalyticsService } from 'base/analytics';
import { ToastQueue } from 'base/ui/toast';
import {
  BlocksService,
  OwnershipType,
  ProjectInfo,
  SearchProjectsPageResponse,
} from 'blocks/blocks_service';
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';

export class ProjectsDataStore {
  @observable projects: ProjectInfo[] = [];
  @observable loading: boolean = false;
  @observable initialLoading: boolean = true; // For the initial load state
  @observable loadingMore: boolean = false;
  @observable error: Error | null = null;
  @observable nextPageUrl: string | null = null;
  @observable projectLimit: number | undefined = undefined;
  @observable projectsUsed: number = 0;
  @observable teamName: string | undefined = undefined;
  @observable removedProjectTokens = new Set<string>();
  @observable flags = {
    hasPaymentProblem: false,
    teamFeaturesRequireUpgrade: false,
  };

  // Dependencies
  private blocksService: BlocksService;
  private accountStore: AccountStore;
  private analytics?: AnalyticsService;
  private toastQueue?: ToastQueue;

  // Filter options - fixed for this store instance
  private ownershipType: OwnershipType | 'all';

  constructor({
    blocksService,
    accountStore,
    analytics,
    toastQueue,
    ownershipType = 'all',
  }: {
    blocksService: BlocksService;
    accountStore: AccountStore;
    analytics?: AnalyticsService;
    toastQueue?: ToastQueue;
    ownershipType?: OwnershipType | 'all';
  }) {
    makeObservable(this);

    this.blocksService = blocksService;
    this.accountStore = accountStore;
    this.analytics = analytics;
    this.toastQueue = toastQueue;
    this.ownershipType = ownershipType;

    // Load data immediately upon store creation
    this.loadProjects();
  }

  @computed
  get hasMore(): boolean {
    return !!this.nextPageUrl;
  }

  @computed
  get atProjectLimit(): boolean {
    return (
      this.projectLimit !== undefined && this.projectsUsed >= this.projectLimit
    );
  }

  @action
  async loadProjects() {
    if (this.loading) return;

    this.loading = true;
    this.error = null;

    try {
      const abortController = new AbortController();

      // Always use searchProjects API for consistency
      // Pass empty query for non-search scenarios, but always include ownership filter
      const response = await this.blocksService.searchProjects(
        true, // Always use pagination for new version
        this.ownershipType,
        abortController.signal,
      );

      // Filter out removed projects
      const visibleProjects = response.projects.filter(
        (p) => !this.removedProjectTokens.has(p.token),
      );

      runInAction(() => {
        this.projects = visibleProjects;
        this.nextPageUrl = response.next || null;
        this.projectLimit = response.projectsQuota.limit;
        this.projectsUsed = response.projectsQuota.used;
        this.teamName = response.teamName;
        this.flags = response.flags;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err : new Error(String(err));
      });
      console.error('Error loading projects:', err);
    } finally {
      runInAction(() => {
        this.loading = false;
        this.initialLoading = false;
      });
    }
  }

  /**
   * Loads the next page of projects and returns data in the format required by InfiniteScroll
   */
  @action
  async loadMore(continuation: string): Promise<{
    continuation?: string;
    items?: ProjectInfo[];
  }> {
    if (!continuation || this.loadingMore || !this.nextPageUrl) {
      return { items: [] };
    }

    this.loadingMore = true;
    this.error = null;

    try {
      const nextPage = await this.blocksService.getSearchProjectsPage(
        continuation,
      );

      // Filter out removed projects
      const newProjects = nextPage.projects.filter(
        (p) => !this.removedProjectTokens.has(p.token),
      );

      runInAction(() => {
        this.projects = [...this.projects, ...newProjects];
        this.nextPageUrl = nextPage.next || null;
      });

      return {
        continuation: nextPage.next || undefined,
        items: newProjects,
      };
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err : new Error(String(err));
      });
      console.error('Error loading more projects:', err);
      return { items: [] };
    } finally {
      runInAction(() => {
        this.loadingMore = false;
      });
    }
  }

  @action
  removeProject(token: string) {
    this.removedProjectTokens.add(token);
    this.projects = this.projects.filter((p) => p.token !== token);
  }

  @computed
  get projectCount(): number | undefined {
    return this.projects.filter((project) => !project.locked).length;
  }

  @computed
  get state():
    | { type: 'loading' }
    | { type: 'error'; error: unknown }
    | { type: 'loaded'; res: SearchProjectsPageResponse } {
    if (this.initialLoading) {
      return { type: 'loading' };
    }
    if (this.error) {
      return { type: 'error', error: this.error };
    }
    return {
      type: 'loaded',
      res: {
        count: this.projects.length,
        next: this.nextPageUrl,
        previous: null,
        projects: this.projects,
        projectsQuota: {
          limit: this.projectLimit,
          used: this.projectsUsed,
        },
        flags: this.flags,
        teamName: this.teamName,
      },
    };
  }
}
